import { useToasts } from 'react-toast-notifications'
import { ChatMessageLinkType } from 'src/graphql.generated'
import { useContext } from 'react'
import { EditMessageContext } from 'src/feed/EditChatMessage/EditMessageProvider'
import {
  ChatMessageLinkedResponseFragment,
  useCreateChatMessageMutation,
  useCreateSmsMessageMutation,
  useEditChatMessageMutation,
} from '../feedQueries.generated'
import { MessageType } from '../useChatDisplayRules'
import { LinkedLesson } from '../mentionLesson'

const useSubmit = (messageCreatedCallback: () => void) => {
  const { stopEditingMessage } = useContext(EditMessageContext)
  const { addToast } = useToasts()
  const [createSmsMessage, { loading: isSendingSmsMessage }] = useCreateSmsMessageMutation({
    onCompleted: () => messageCreatedCallback(),
    onError(error) {
      addToast(error.message)
    },
  })
  const [createChatMessage, { loading: isSendingChatMessage }] = useCreateChatMessageMutation({
    onError() {
      addToast('Message sending failed! Please try again.')
    },
  })

  const [editChatMessage, { loading: isEditingChatMessage }] = useEditChatMessageMutation({
    onError(error) {
      addToast(error.message)
    },
    onCompleted: (data) => {
      if (data.editChatMessage) {
        addToast('Message edited successfully!', { appearance: 'success', autoDismissTimeout: 1000 })
        stopEditingMessage()
      }
    },
  })

  const isSendingMessage = isSendingSmsMessage || isSendingChatMessage || isEditingChatMessage

  const buildLinksTo = (mentionedResponse?: ChatMessageLinkedResponseFragment, mentionedLesson?: LinkedLesson) => {
    if (mentionedLesson) {
      return { type: ChatMessageLinkType.Lesson, id: mentionedLesson.lessonID }
    }
    if (mentionedResponse) {
      return { type: ChatMessageLinkType.Response, id: mentionedResponse.responseID }
    }
    return null
  }

  return {
    createMessage: (
      messageType: MessageType,
      uid: string,
      text: string,
      mentionedResponse?: ChatMessageLinkedResponseFragment,
      mentionedLesson?: LinkedLesson
    ) =>
      messageType === MessageType.SMS
        ? createSmsMessage({
            variables: {
              uid,
              text,
            },
          })
        : createChatMessage({
            variables: {
              uid,
              text,
              linksTo: buildLinksTo(mentionedResponse, mentionedLesson),
            },
          }),
    editMessage: (uid: string, messageIDHash: string, text: string) =>
      editChatMessage({
        variables: { uid, messageIDHash, text },
      }),
    isSendingMessage,
  }
}

export default useSubmit
